import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Users, Search, Download, Settings, Shield, Save, CheckCircle2 } from 'lucide-react';
import AdminDashboard from './admin/AdminDashboard';
import AdminUsers from './admin/AdminUsers';
import AdminHistory from './admin/AdminHistory';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ── Settings Panel ─────────────────────────────────────────────────────────
function AdminSettings() {
  const [upiId,   setUpiId]  = useState('');
  const [loading, setLoading]= useState(true);
  const [saving,  setSaving] = useState(false);
  const [saved,   setSaved]  = useState(false);

  useEffect(() => {
    api.get('/admin/settings')
      .then(r => { setUpiId(r.data.settings?.upi_id || ''); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!upiId.trim()) { toast.error('Enter a UPI ID'); return; }
    setSaving(true);
    try {
      await api.post('/admin/settings', { upi_id: upiId.trim() });
      toast.success('UPI ID updated');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="empty-state"><div className="spinner spinner-lg"/></div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, marginBottom:6 }}>Settings</h2>
      <p style={{ color:'var(--text3)', fontSize:14, marginBottom:28 }}>Configure payment and platform settings.</p>

      {/* Payment Settings */}
      <div className="card" style={{ padding:'24px', marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>💳 Payment Settings</div>
        <p style={{ color:'var(--text3)', fontSize:13.5, marginBottom:20 }}>
          UPI ID shown to users during plan purchase. Users pay to this ID and submit their transaction number for verification.
        </p>

        <div className="form-group" style={{ marginBottom:16 }}>
          <label className="form-label">UPI ID *</label>
          <input
            className="input"
            value={upiId}
            onChange={e => setUpiId(e.target.value)}
            placeholder="e.g. support@prepb.com or 9999999999@paytm"
          />
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:5 }}>
            This is the UPI ID users will send payment to. Can be any valid UPI ID (Google Pay, PhonePe, Paytm, bank UPI).
          </div>
        </div>

        <div style={{ background:'var(--bg3)', borderRadius:9, padding:'12px 14px', fontSize:13, color:'var(--text2)', marginBottom:16, lineHeight:1.7 }}>
          <strong>Current payment flow:</strong><br/>
          1. User selects plan → sees your UPI ID<br/>
          2. User pays via any UPI app<br/>
          3. User submits transaction ID<br/>
          4. You verify in Admin → Subscriptions and activate their plan
        </div>

        <button
          className="btn btn-primary"
          onClick={save}
          disabled={saving}
          style={{ display:'flex', alignItems:'center', gap:8 }}
        >
          {saving ? <><div className="spinner spinner-sm"/>Saving…</> :
           saved  ? <><CheckCircle2 size={14}/>Saved!</> :
                    <><Save size={14}/>Save UPI ID</>}
        </button>
      </div>

      {/* Info */}
      <div className="card" style={{ padding:'20px', background:'var(--accent-bg)', border:'1px solid var(--accent)' }}>
        <div style={{ fontWeight:700, fontSize:15, marginBottom:8 }}>📋 PhonePe Gateway</div>
        <p style={{ color:'var(--text2)', fontSize:13.5, lineHeight:1.7 }}>
          To enable automatic PhonePe payments (no manual verification needed), add these to your <code style={{ background:'var(--bg3)', padding:'1px 5px', borderRadius:4 }}>.env</code> file:
        </p>
        <pre style={{ background:'var(--bg3)', borderRadius:8, padding:'12px 14px', fontSize:12.5, marginTop:12, overflow:'auto', lineHeight:1.8 }}>
{`PHONEPE_MERCHANT_ID=your_merchant_id
PHONEPE_SALT_KEY=your_salt_key
PHONEPE_SALT_INDEX=1
PHONEPE_ENV=sandbox`}
        </pre>
        <p style={{ color:'var(--text3)', fontSize:12.5, marginTop:10 }}>
          Get credentials at <a href="https://developer.phonepe.com" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>developer.phonepe.com</a>. Change to <code style={{ background:'var(--bg3)', padding:'1px 4px', borderRadius:4 }}>PHONEPE_ENV=production</code> when ready.
        </p>
      </div>
    </div>
  );
}

// ── Main AdminPage ─────────────────────────────────────────────────────────
const NAV = [
  { id:'dashboard', label:'Dashboard',       icon:<LayoutDashboard size={16}/> },
  { id:'users',     label:'User Management', icon:<Users size={16}/>           },
  { id:'history',   label:'Search History',  icon:<Search size={16}/>          },
  { id:'settings',  label:'Settings',        icon:<Settings size={16}/>        },
];

export default function AdminPage() {
  const [active, setActive] = useState('dashboard');

  const exportCSV = async (endpoint, filename) => {
    try {
      const r = await api.get(endpoint, { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href=url; a.download=filename; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div style={{ padding:'0 16px 16px', borderBottom:'1px solid var(--border)', marginBottom:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 0 4px' }}>
            <div style={{ width:30, height:30, background:'linear-gradient(135deg,var(--accent),var(--accent2))', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Shield size={16} color="#fff"/>
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>Admin Panel</div>
              <div style={{ fontSize:11, color:'var(--text3)' }}>Full access</div>
            </div>
          </div>
        </div>

        <div className="admin-sidebar-title">Management</div>
        {NAV.map(n => (
          <button key={n.id} className={`admin-nav-item ${active===n.id?'active':''}`} onClick={() => setActive(n.id)}>
            {n.icon}{n.label}
          </button>
        ))}

        <div className="admin-sidebar-title">Quick Export</div>
        <button className="admin-nav-item" onClick={() => exportCSV('/admin/export/users', 'users.csv')}>
          <Download size={15}/> Export Users CSV
        </button>
        <button className="admin-nav-item" onClick={() => exportCSV('/admin/export/search-history', 'search_history.csv')}>
          <Download size={15}/> Export History CSV
        </button>
      </aside>

      {/* Content */}
      <div className="admin-content">
        {active === 'dashboard' && <AdminDashboard/>}
        {active === 'users'     && <AdminUsers/>}
        {active === 'history'   && <AdminHistory/>}
        {active === 'settings'  && <AdminSettings/>}
      </div>
    </div>
  );
}